import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaWhatsapp } from 'react-icons/fa';
import { LuArrowLeft } from 'react-icons/lu';

const WHATSAPP_URL = 'https://api.whatsapp.com/send/?phone=552132798800';

const VARIANTS = {
  pendente: {
    title: 'Contrato aguardando confirmação',
    message: 'Olá! Realizei uma contratação pelo e-commerce da Santa Casa Copacabana recentemente, mas não consegui encontrar o link de confirmação enviado. Gostaria de ajuda para finalizar minha contratação.',
    paragraphs: [
      'Identificamos um contrato recente criado via e-commerce para este CPF que ainda não foi confirmado pelo WhatsApp ou pelo e-mail.',
      'Por favor, acesse os meios de contato informados no cadastro e siga as instruções para concluir sua contratação.',
      'Caso esteja tendo problemas para encontrar o link, entre em contato com nosso atendimento pelo botão abaixo.',
    ],
  },
  inapto: {
    title: 'Vamos resolver por aqui',
    message: 'Olá! Tentei realizar uma contratação pelo e-commerce da Santa Casa Copacabana, mas não consegui continuar online. Gostaria de entender como posso prosseguir.',
    paragraphs: [
      'Não é possível finalizar esta contratação online com este CPF.',
      'Fale com a gente pelo WhatsApp e a equipe te ajuda a seguir com a contratação.',
    ],
  },
  confirmacao: {
    title: 'Erro na confirmação do cadastro',
    message: 'Olá! Tentei confirmar meu cadastro pelo link do e-commerce da Santa Casa Copacabana, mas ocorreu um erro ao gerar o cadastro no sistema. Gostaria de ajuda para concluir a contratação.',
    paragraphs: [
      'Não foi possível concluir a confirmação do seu cadastro neste momento.',
      'Tente novamente em alguns instantes. Se o problema continuar, fale com o atendimento pelo WhatsApp — a mensagem já vai pronta para agilizar o suporte.',
    ],
  },
};

export const getWhatsAppLink = (message) => {
  const params = new URLSearchParams({
    text: message,
    type: 'phone_number',
    app_absent: '0',
  });
  return `${WHATSAPP_URL}&${params.toString()}`;
};

export const getSupportWhatsAppLink = (variant) => {
  const content = VARIANTS[variant];
  if (!content) return WHATSAPP_URL;
  return getWhatsAppLink(content.message);
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  box-shadow: ${props => props.theme.shadows.xl};
  animation: ${slideUp} 0.2s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 95%;
  }
`;

const ModalHeader = styled.div`
  background: ${props => props.theme.colors.primary};
  padding: 28px 24px;
  text-align: center;
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: ${props => props.theme.colors.white};
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.colors.white};
  font-size: 22px;
  font-weight: 700;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 28px 24px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.7;
  margin: 0 0 14px 0;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.theme.colors.background};
  padding: 14px 20px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #059669;
  text-decoration: none;
  border: 1px solid #10b981;
  margin-top: 20px;
  transition: background-color 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: #ecfdf5;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 12px 16px;
  }
`;

const ButtonRow = styled.div`
  margin-top: 24px;
`;

const CloseButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.grayLight};
  }
`;

const SupportModal = ({ open, onClose, variant }) => {
  const content = VARIANTS[variant];
  if (!content) return null;

  return (
    <ModalOverlay open={open}>
      <ModalContainer>
        <ModalHeader>
          <IconWrap>
            <FaWhatsapp size={28} />
          </IconWrap>
          <ModalTitle>{content.title}</ModalTitle>
        </ModalHeader>

        <ModalBody>
          {content.paragraphs.map((paragraph, index) => (
            <InfoText key={index}>{paragraph}</InfoText>
          ))}

          <ContactLink
            href={getWhatsAppLink(content.message)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp size={20} />
            Falar com o atendimento
          </ContactLink>

          <ButtonRow>
            <CloseButton type="button" onClick={onClose}>
              <LuArrowLeft size={18} />
              Voltar
            </CloseButton>
          </ButtonRow>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default SupportModal;
